import { createBrowserRouter, Outlet, useRoutes } from 'react-router-dom'
import { AppRouteObject, MetaProvider } from '@contexts/Meta'
import { OptionsProvider } from '@contexts/Options'
import { ConfigurationProvider } from '@contexts/ConfigurationPage'
import { LandingPage } from '@pages/Landing/LandingPage'
import { About } from '@pages/Configurations/About.tsx/AboutPage'
import { Configurations } from '@pages/Configurations/Configurations'
import { Users } from '@pages/Configurations/UsersConfig/Users'
import { SmsRecipients } from '@pages/Configurations/UsersConfig/SmsRecipient'
import { EditSmsRecipient } from '@pages/Configurations/UsersConfig/EditSmsRecipient'
import { CreateUsers } from '@pages/Configurations/UsersConfig/CreateUsers'
import { EditUser } from '@pages/Configurations/UsersConfig/EditUser'
import { ProfileSetting } from '@pages/Configurations/Settings/ProfileSetting'
import { Projects } from '@pages/Configurations/UsersConfig/Projects'
import { CreateUserGroups } from '@pages/Configurations/UsersConfig/CreateUserGroup'
import { EditUserGroups } from '@pages/Configurations/UsersConfig/EditUserGroup'
import { UserGroups } from '@pages/Configurations/UsersConfig/UserGroups'
import UnauthorizedPage from '@pages/Error/401'
import NotFoundPage from '@pages/Error/404'
import SessionTimeOutPage from '@pages/Error/SessionTimeOutPage'
import { ProtectedRoute } from './protectedRoute'
import { ErrorRoute } from './errorRoute'
import { PermissionCodeEnum } from '@models/permission'
import { AuthProvider } from '@contexts/Auth'


const Providers = () => (
	<AuthProvider>
	<OptionsProvider>
		<ConfigurationProvider>
			<MetaProvider>
				<Outlet />
			</MetaProvider>
		</ConfigurationProvider>
	</OptionsProvider>
	</AuthProvider>
)

const routes: AppRouteObject[] = [
	{
		path: '/',
		element: <Providers />,
		children: [
			{
				path: '/',
				element: <ProtectedRoute children={<LandingPage />} />,
				
			},
			{
				path: '/configurations',
				element: (
					<ProtectedRoute
						children={<Configurations />}
						permissionCodes={[PermissionCodeEnum.ConfigurationMenu]}
					/>
				),
				children: [
					{
						path: '/configurations/users_config',
						element: (
							<ProtectedRoute
								children={<Users />}
								permissionCodes={[
									PermissionCodeEnum.UserCreate,
									PermissionCodeEnum.UserRead,
									PermissionCodeEnum.UserUpdate,
									PermissionCodeEnum.UserDelete,
									PermissionCodeEnum.UserResetPassword,
									PermissionCodeEnum.UserAssignRole,
								]}
							/>
						),
					},
					{
						path: '/configurations/users_config/users',
						element: (
							<ProtectedRoute
								children={<Users />}
								permissionCodes={[
									PermissionCodeEnum.UserCreate,
									PermissionCodeEnum.UserRead,
									PermissionCodeEnum.UserUpdate,
									PermissionCodeEnum.UserDelete,
									PermissionCodeEnum.UserResetPassword,
									PermissionCodeEnum.UserAssignRole,
								]}
							/>
						),
					},
					{
						path: '/configurations/users_config/users/create_user',
						element: (
							<ProtectedRoute
								children={<CreateUsers />}
								permissionCodes={[PermissionCodeEnum.UserCreate]}
							/>
						),
					},
					{
						path: '/configurations/users_config/projects/user_groups/create_user_group/:project_id',
						element: (
							<ProtectedRoute
								children={<CreateUserGroups />}
								permissionCodes={[PermissionCodeEnum.RoleCreate]}
							/>
						),
					},
					{
						path: '/configurations/users_config/projects/user_groups/edit_user_group/:project_id/:role_id',
						element: (
							<ProtectedRoute
								children={<EditUserGroups />}
								permissionCodes={[PermissionCodeEnum.RoleUpdate]}
							/>
						),
					},
					{
						path: '/configurations/users_config/users/edit_user/:id',
						element: (
							<ProtectedRoute
								children={<EditUser />}
								permissionCodes={[PermissionCodeEnum.UserUpdate]}
							/>
						),
					},
					{
						path: '/configurations/users_config/user_groups/projects',
						element: (
							<ProtectedRoute
								children={<Projects />}
								permissionCodes={[
									PermissionCodeEnum.RoleCreate,
									PermissionCodeEnum.RoleUpdate,
									PermissionCodeEnum.RoleRead,
									PermissionCodeEnum.RoleDelete,
								]}
							/>
						),
					},
					{
						path: '/configurations/users_config/user_groups/:project_id',
						element: (
							<ProtectedRoute
								children={<UserGroups />}
								permissionCodes={[
									PermissionCodeEnum.RoleCreate,
									PermissionCodeEnum.RoleUpdate,
									PermissionCodeEnum.RoleRead,
									PermissionCodeEnum.RoleDelete,
								]}
							/>
						),
					},
					{
						path: '/configurations/users_config/sms_recipients',
						element: (
							<ProtectedRoute
								children={<SmsRecipients />}
								permissionCodes={[/* Add appropriate permissions */]}
							/>
						),
						handle: {
							hideSidebar: true,
						}
					},
					
				{
					path: '/configurations/users_config/sms_recipients/create',
					element: (
						<ProtectedRoute
							children={<EditSmsRecipient />}
							permissionCodes={[/* Add appropriate permissions */]}
						/>
					),
					handle: {
						hideSidebar: true,
					}
				},
				{
					path: '/configurations/users_config/sms_recipients/edit/:id',
					element: (
						<ProtectedRoute
							children={<EditSmsRecipient />}
							permissionCodes={[/* Add appropriate permissions */]}
						/>
					),
					handle: {
						hideSidebar: true,
					}
				},
					{
						path: '/configurations/settings',
						element: <ProfileSetting />,
					},
					{
						path: '/configurations/settings/profile-setting',
						element: <ProfileSetting />,
					},
					{
						path: '/configurations/about',
						element: <About />,
					},
				],
			},

			{
				path: '/401',
				element: <ErrorRoute children={<UnauthorizedPage />} />,
			},
			{
				path: '/404',
				element: <ErrorRoute children={<NotFoundPage />} />,
			},
			{
				path: '*',
				element: <ErrorRoute children={<NotFoundPage />} />,
			},
		],
	},
	{
		path: '/session-timeout',
		element: <SessionTimeOutPage />,
	},
];

export const MainRoutes = () => {
	const routeResult = useRoutes(
		routes
	)
	return routeResult
}

export const router = createBrowserRouter(routes);
